// Reprotech Livestock Management Platform - Production Application
// Comprehensive veterinary biotechnology system with 30+ specialized modules
// Features: Animal management, genomics, reproduction, laboratory, farm operations
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { NotificationProvider } from './components/notifications/NotificationProvider'
import Layout from './components/Layout'
import DashboardRouter from './pages/dashboard/DashboardRouter'
import Login from './pages/Login'
import ProtectedRoute from './components/ProtectedRoute'
import React, { Suspense } from 'react'

// Import integration test for development
import './utils/integrationTest'

// Production modules - comprehensive livestock management system
const Animals = React.lazy(() => import('./modules/animals'))
const AnimalAssignments = React.lazy(() => import('./modules/animal-assignments'))
const Finance = React.lazy(() => import('./modules/finance'))
const Admin = React.lazy(() => import('./modules/admin'))
const Laboratory = React.lazy(() => import('./modules/laboratory'))
const MediaPreparation = React.lazy(() => import('./modules/media-preparation'))
const Inventory = React.lazy(() => import('./modules/inventory'))
const InventoryAnalytics = React.lazy(() => import('./modules/inventory-analytics'))
const Genomics = React.lazy(() => import('./modules/genomics'))
const GenomicIntelligence = React.lazy(() => import('./modules/genomic-intelligence'))
const FarmManagement = React.lazy(() => import('./modules/farm-management'))
const FarmAnalytics = React.lazy(() => import('./modules/farm-analytics'))
const FarmCompliance = React.lazy(() => import('./modules/farm-compliance'))
const DropletingManagement = React.lazy(() => import('./modules/milking-management'))
const FatteningManagement = React.lazy(() => import('./modules/fattening-management'))
const Breeding = React.lazy(() => import('./modules/breeding'))
const Biobank = React.lazy(() => import('./modules/biobank'))
const Settings = React.lazy(() => import('./modules/settings'))

// Additional production modules
const EmbryoTransfer = React.lazy(() => import('./modules/embryo-transfer'))
const EmbryoDetail = React.lazy(() => import('./modules/embryo-detail'))
const Vaccinations = React.lazy(() => import('./modules/vaccinations'))
const Ultrasound = React.lazy(() => import('./modules/ultrasound'))
const OPU = React.lazy(() => import('./modules/opu'))
const SemenManagement = React.lazy(() => import('./modules/semen-management'))
const Reproduction = React.lazy(() => import('./modules/reproduction'))
const Fertilization = React.lazy(() => import('./modules/fertilization'))
const Flushing = React.lazy(() => import('./modules/flushing'))
const Phenotype = React.lazy(() => import('./modules/phenotype'))
const AnalyticsDashboard = React.lazy(() => import('./modules/analytics-dashboard'))
const RealtimeMonitoring = React.lazy(() => import('./modules/realtime-monitoring'))

// Specialized modules
const SNPAnalysis = React.lazy(() => import('./modules/snp-analysis'))
const BeadchipMappings = React.lazy(() => import('./modules/beadchip-mappings'))
const SampleManagement = React.lazy(() => import('./modules/sample-management'))
const LabResults = React.lazy(() => import('./modules/lab-results'))
const Equipment = React.lazy(() => import('./modules/equipment'))
const Calendar = React.lazy(() => import('./modules/calendar'))
const Research = React.lazy(() => import('./modules/research'))
const Customers = React.lazy(() => import('./modules/customers'))
const Tenders = React.lazy(() => import('./modules/tenders'))
const HumanResources = React.lazy(() => import('./modules/human-resources'))
const DataIntegration = React.lazy(() => import('./modules/data-integration'))
const IntegrationHub = React.lazy(() => import('./modules/integration-hub'))

// Medical and clinical modules
const InternalMedicine = React.lazy(() => import('./modules/internal-medicine'))
const ClinicalHub = React.lazy(() => import('./modules/clinical-hub'))

// AI and procurement modules
const AIAnalytics = React.lazy(() => import('./modules/ai-analytics'))
const ProcurementManagement = React.lazy(() => import('./modules/procurement-management'))

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
)

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DashboardRouter />} />
          
          {/* Core Animal Management */}
          <Route path="modules/animals/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Animals />
            </Suspense>
          } />
          <Route path="modules/animal-assignments/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AnimalAssignments />
            </Suspense>
          } />
          <Route path="modules/breeding/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Breeding />
            </Suspense>
          } />
          <Route path="modules/embryo-transfer/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <EmbryoTransfer />
            </Suspense>
          } />
          <Route path="modules/embryo-detail/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <EmbryoDetail />
            </Suspense>
          } />
          <Route path="modules/reproduction/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Reproduction />
            </Suspense>
          } />
          <Route path="modules/fertilization/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Fertilization />
            </Suspense>
          } />
          <Route path="modules/flushing/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Flushing />
            </Suspense>
          } />
          <Route path="modules/phenotype/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Phenotype />
            </Suspense>
          } />

          {/* Veterinary & Medical */}
          <Route path="modules/vaccinations/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Vaccinations />
            </Suspense>
          } />
          <Route path="modules/ultrasound/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Ultrasound />
            </Suspense>
          } />
          <Route path="modules/opu/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <OPU />
            </Suspense>
          } />
          <Route path="modules/internal-medicine/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <InternalMedicine />
            </Suspense>
          } />
          <Route path="modules/clinical-hub/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ClinicalHub />
            </Suspense>
          } />

          {/* Laboratory & Genomics */}
          <Route path="modules/laboratory/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Laboratory />
            </Suspense>
          } />
          <Route path="modules/media-preparation/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <MediaPreparation />
            </Suspense>
          } />
          <Route path="modules/genomics/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Genomics />
            </Suspense>
          } />
          <Route path="modules/genomic-intelligence/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <GenomicIntelligence />
            </Suspense>
          } />
          <Route path="modules/biobank/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Biobank />
            </Suspense>
          } />
          <Route path="modules/sample-management/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <SampleManagement />
            </Suspense>
          } />
          <Route path="modules/lab-results/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <LabResults />
            </Suspense>
          } />
          <Route path="modules/snp-analysis/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <SNPAnalysis />
            </Suspense>
          } />
          <Route path="modules/beadchip-mappings/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <BeadchipMappings />
            </Suspense>
          } />
          <Route path="modules/semen-management/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <SemenManagement />
            </Suspense>
          } />

          {/* Farm Operations */}
          <Route path="modules/farm-management/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <FarmManagement />
            </Suspense>
          } />
          <Route path="modules/farm-analytics/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <FarmAnalytics />
            </Suspense>
          } />
          <Route path="modules/farm-compliance/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <FarmCompliance />
            </Suspense>
          } />
          <Route path="modules/milking-management/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <DropletingManagement />
            </Suspense>
          } />
          <Route path="modules/fattening-management/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <FatteningManagement />
            </Suspense>
          } />
          <Route path="modules/inventory/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Inventory />
            </Suspense>
          } />
          <Route path="modules/inventory-analytics/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <InventoryAnalytics />
            </Suspense>
          } />
          <Route path="modules/equipment/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Equipment />
            </Suspense>
          } />
          <Route path="modules/procurement-management/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ProcurementManagement />
            </Suspense>
          } />

          {/* Business Management */}
          <Route path="modules/finance/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Finance />
            </Suspense>
          } />
          <Route path="modules/customers/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Customers />
            </Suspense>
          } />
          <Route path="modules/tenders/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Tenders />
            </Suspense>
          } />
          <Route path="modules/human-resources/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <HumanResources />
            </Suspense>
          } />

          {/* Analytics & AI */}
          <Route path="modules/analytics-dashboard/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AnalyticsDashboard />
            </Suspense>
          } />
          <Route path="modules/ai-analytics/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AIAnalytics />
            </Suspense>
          } />
          <Route path="modules/realtime-monitoring/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <RealtimeMonitoring />
            </Suspense>
          } />

          {/* System Management */}
          <Route path="modules/admin/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Admin />
            </Suspense>
          } />
          <Route path="modules/settings/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Settings />
            </Suspense>
          } />
          <Route path="modules/calendar/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Calendar />
            </Suspense>
          } />
          <Route path="modules/research/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Research />
            </Suspense>
          } />
          <Route path="modules/data-integration/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <DataIntegration />
            </Suspense>
          } />
          <Route path="modules/integration-hub/*" element={
            <Suspense fallback={<LoadingSpinner />}>
              <IntegrationHub />
            </Suspense>
          } />
        </Route>
      </Routes>
    </AuthProvider>
    </NotificationProvider>
  )
}

export default App
